import { kebabCase } from 'lodash';
import { pascalCase } from 'utils/pascal-case';
import { unreachable } from 'utils/unreachable';
import { EstimateField } from '../fields';
import { TermSeparator } from './separators';

export enum EstimateFieldFilterOperationType {
  IsMoreThan = 'IsMoreThan',
  IsLessThan = 'IsLessThan',
}

interface EstimateFieldFilterBase {
  field: EstimateField;
}

export enum EstimateRelativeDirection {
  Late = 'Late',
  Early = 'Early',
}

interface EstimateFieldRelativeFilterBase extends EstimateFieldFilterBase {
  direction: EstimateRelativeDirection;
  days?: number;
}

interface DateFieldIsMoreThanFilter extends EstimateFieldRelativeFilterBase {
  type: EstimateFieldFilterOperationType.IsMoreThan;
}

interface DateFieldIsLessThanFilter extends EstimateFieldRelativeFilterBase {
  type: EstimateFieldFilterOperationType.IsLessThan;
}

export type EstimateFieldRelativeFilter =
  | DateFieldIsMoreThanFilter
  | DateFieldIsLessThanFilter;

export type EstimateFieldFilter = EstimateFieldRelativeFilter;

const isValid = (filter: EstimateFieldFilter) => {
  switch (filter.type) {
    case EstimateFieldFilterOperationType.IsMoreThan:
    case EstimateFieldFilterOperationType.IsLessThan:
      return filter.days != null;
    default:
      return true;
  }
};

const toString = (filter: EstimateFieldFilter): string => {
  const type = kebabCase(filter.type);

  switch (filter.type) {
    case EstimateFieldFilterOperationType.IsMoreThan:
    case EstimateFieldFilterOperationType.IsLessThan:
      return [
        type,
        `${JSON.stringify(filter.days)}~${kebabCase(filter.direction)}`,
      ].join(TermSeparator);
    default:
      return unreachable(filter);
  }
};

const parse = (value: string, field: EstimateField): EstimateFieldFilter => {
  const [type_string, meta] = value.split(TermSeparator);
  const type = pascalCase(type_string) as EstimateFieldFilterOperationType;

  switch (type) {
    case EstimateFieldFilterOperationType.IsMoreThan:
    case EstimateFieldFilterOperationType.IsLessThan: {
      const [days, direction] = meta.split('~');

      return {
        field,
        type,
        days: JSON.parse(days),
        direction: pascalCase(direction) as EstimateRelativeDirection,
      };
    }
    default:
      return unreachable(type);
  }
};

export const EstimateFieldFilter = {
  isValid,
  toString,
  parse,
};
